import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../../Home/sidebarSections';
import { NAV_ROUTES } from '../../Home/navRoutes';
import { PAGE_MAX_WIDTH } from '../../../theme';
import { fetchSisMatchingList, type SisListRow } from './sisMatchingApi';
import { feedLine, filterRows, LIST_FILTERS, matchingSubLabel, plural, rowCounts, statusPill, valuesCell, type ListFilter } from './sisMatchingView';
import { SIS, sisText } from './sisTokens';
import { SisPill } from './SisPill';
import { HEIGHT_BELOW_BARS } from '../../../core/bannerOffset';

type Props = {
  source?: () => Promise<SisListRow[]>;
};

const LOAD_FAILED = 'Couldn’t load the districts. Refresh to try again.';

const column = { display: 'flex', flexDirection: 'column' } as const;
const lanes = { district: { flex: 1, minWidth: 0 }, feed: { width: 168, flexShrink: 0 }, fields: { width: 136, flexShrink: 0 }, values: { width: 176, flexShrink: 0 }, matching: { width: 184, flexShrink: 0 }, chevron: { width: 20, flexShrink: 0 } } as const;

export function AdminSisMatchingPage({ source = fetchSisMatchingList }: Props = {}) {
  const [rows, setRows] = useState<SisListRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ListFilter>('all');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const all = rows ?? [];
  const counts = rowCounts(all);
  const shown = filterRows(all, query, filter);

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SIS.background }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, padding: '24px' }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, margin: '0 auto', ...column, gap: '24px' }}>
          <Box sx={{ ...column, gap: '4px' }}>
            <Typography component="h1" sx={sisText.h1}>SIS matching</Typography>
            {rows !== null && (
              <Typography sx={{ ...sisText.body1, color: SIS.muted }}>
                {`${plural(counts.districts, 'district')} · ${counts.withFeed} with a RosterStream feed`}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Dot color={SIS.warning} text={`${counts.attention} need attention`} strong />
              <Dot color={SIS.success} text={`${counts.confirmed} confirmed`} />
              <Dot color={SIS.dotGrey} text={`${counts.noFeed} without a feed`} quiet />
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: 280, height: 40, padding: '0 12px', border: `1px solid ${SIS.dotGrey}`, borderRadius: '8px', bgcolor: SIS.surface }}>
                <SearchIcon sx={{ width: 16, height: 16, color: SIS.muted }} />
                <Box
                  component="input"
                  type="search"
                  aria-label="Find a district"
                  placeholder="Find a district"
                  value={query}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                  sx={{ ...sisText.body1, border: 0, outline: 'none', background: 'none', width: '100%', padding: 0, '&::placeholder': { color: SIS.disabled } }}
                />
              </Box>
              <Box
                component="select"
                aria-label="Which districts to show"
                value={filter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilter(e.target.value as ListFilter)}
                sx={{ ...sisText.body1, fontWeight: 500, height: 40, padding: '0 12px', border: `1px solid ${SIS.dotGrey}`, borderRadius: '8px', bgcolor: SIS.surface }}
              >
                {LIST_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </Box>
            </Box>
          </Box>
          {failed && <Typography sx={{ ...sisText.body1, color: SIS.muted }}>{LOAD_FAILED}</Typography>}
          {!failed && rows === null && <CircularProgress data-testid="sis-list-loading" size={24} />}
          {!failed && rows !== null && (
            <Box component="section" aria-label="Districts" sx={{ bgcolor: SIS.surface, borderRadius: '8px', boxShadow: SIS.cardShadow, overflow: 'hidden' }}>
              <Box data-testid="sis-list-header" sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 24px', bgcolor: SIS.headerBg, boxShadow: `inset 0 -1px 0 ${SIS.divider}` }}>
                <Typography sx={{ ...sisText.body2, ...lanes.district }}>District</Typography>
                <Typography sx={{ ...sisText.body2, ...lanes.feed }}>RosterStream feed</Typography>
                <Typography sx={{ ...sisText.body2, ...lanes.fields }}>Fields</Typography>
                <Typography sx={{ ...sisText.body2, ...lanes.values }}>Values</Typography>
                <Typography sx={{ ...sisText.body2, ...lanes.matching }}>Matching</Typography>
                <Box sx={lanes.chevron} />
              </Box>
              {shown.length === 0 && (
                <Typography sx={{ ...sisText.body1, color: SIS.muted, padding: '32px 24px' }}>No district matches.</Typography>
              )}
              {shown.map((row) => (
                <Row key={row.districtId} row={row} onOpen={() => navigate(`${NAV_ROUTES.sisMatchingAdmin}/${encodeURIComponent(row.districtId)}`)} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function Dot({ color, text, strong, quiet }: { color: string; text: string; strong?: boolean; quiet?: boolean }) {
  return (
    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <Box component="i" sx={{ width: 10, height: 10, borderRadius: '9999px', bgcolor: color, display: 'inline-block' }} />
      <Typography sx={{ ...sisText.body1, fontWeight: strong ? 700 : 400, color: quiet ? SIS.muted : SIS.ink }}>{text}</Typography>
    </Box>
  );
}

function Row({ row, onOpen }: { row: SisListRow; onOpen: () => void }) {
  const open = row.feed !== null && row.matching !== 'no_feed';
  const rowSx = { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 24px', width: '100%', border: 0, background: 'none', textAlign: 'left', '& + &': { boxShadow: `inset 0 1px 0 ${SIS.divider}` } } as const;
  const sub = `${row.state ? `${row.state} · ` : ''}${plural(row.students, 'student')}`;
  if (!open) {
    return (
      <Box data-testid="sis-list-row" aria-disabled="true" sx={rowSx}>
        <Box sx={{ ...column, ...lanes.district }}>
          <Typography data-testid="sis-district-name" sx={sisText.body1Strong}>{row.districtName}</Typography>
          <Typography sx={sisText.caption}>{sub}</Typography>
        </Box>
        <Typography sx={{ ...sisText.body1, color: SIS.muted, ...lanes.feed }}>No feed</Typography>
        <Typography sx={{ ...sisText.body1, color: SIS.disabled, ...lanes.fields }}>—</Typography>
        <Typography sx={{ ...sisText.body1, color: SIS.disabled, ...lanes.values }}>—</Typography>
        <Typography sx={{ ...sisText.body1, color: SIS.disabled, ...lanes.matching }}>—</Typography>
        <Box sx={lanes.chevron} />
      </Box>
    );
  }
  const values = valuesCell(row);
  return (
    <Box component="button" type="button" data-testid="sis-list-row" onClick={onOpen} sx={{ ...rowSx, cursor: 'pointer', '&:hover': { bgcolor: SIS.hoverBg } }}>
      <Box sx={{ ...column, ...lanes.district }}>
        <Typography data-testid="sis-district-name" sx={{ ...sisText.body1Strong, color: SIS.primary }}>{row.districtName}</Typography>
        <Typography sx={sisText.caption}>{sub}</Typography>
      </Box>
      <Box sx={{ ...column, ...lanes.feed }}>
        <Typography sx={sisText.body1}>Connected</Typography>
        <Typography sx={sisText.caption}>{feedLine(row.feed, row.summaryAsOf)}</Typography>
      </Box>
      <Box sx={{ ...column, ...lanes.fields }}>
        <Typography sx={sisText.body1}>{`${row.answered} of ${row.fieldsTotal}`}</Typography>
        {row.notInSis > 0 && <Typography sx={sisText.caption}>{`${row.notInSis} not in this SIS`}</Typography>}
      </Box>
      <Box sx={{ ...column, ...lanes.values }}>
        <Typography sx={{ ...sisText.body1, fontWeight: values.warn ? 700 : 400, color: values.warn ? SIS.warningDark : SIS.ink }}>{values.text}</Typography>
        {values.sub && <Typography sx={{ ...sisText.caption, color: values.warn ? SIS.warningDark : SIS.muted }}>{values.sub}</Typography>}
      </Box>
      <Box sx={{ ...column, alignItems: 'flex-start', ...lanes.matching }}>
        <SisPill pill={statusPill(row.matching)} />
        <Typography sx={sisText.caption}>{matchingSubLabel(row)}</Typography>
      </Box>
      <ChevronRightIcon sx={{ ...lanes.chevron, color: SIS.muted }} />
    </Box>
  );
}
